import React, { useState, useEffect, useRef } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const OfflineScreen = ({ onReconnect }) => {
  const { language } = useLanguage();
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showReconnected, setShowReconnected] = useState(false);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);

      if (onReconnect) {
        onReconnect();
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }

      reconnectTimerRef.current = setTimeout(() => {
        setShowReconnected(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };

    // Dubbelkolla status när skärmen väcks från dvala
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentlyOnline = navigator.onLine;
        if (currentlyOnline && !isOnline) {
          handleOnline();
        } else if (!currentlyOnline && isOnline) {
          handleOffline();
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [isOnline, onReconnect]);

  // Grön banner vid återanslutning
  if (showReconnected) {
    return (
      <aside
        aria-label="Nätverksstatus"
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] bg-emerald-600 text-white px-5 py-2.5 rounded-full shadow-lg flex items-center gap-2.5 text-xs md:text-sm font-semibold tracking-wide transition-all duration-300 pointer-events-none"
      >
        <Wifi size={16} className="text-white flex-none" />
        <span>
          {t(language, 'offline_reconnected') || 'Ansluten igen — synkroniserar...'}
        </span>
      </aside>
    );
  }

  // Om uppkopplad, rendera ingenting
  if (isOnline) return null;

  // Diskret flytande offline-banner
  return (
    <aside
      aria-label="Nätverksstatus"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] bg-slate-900/90 text-white backdrop-blur-md px-5 py-2.5 rounded-full shadow-xl border border-slate-700/60 flex items-center gap-3 text-xs md:text-sm font-medium tracking-wide transition-all duration-300"
    >
      <span className="relative flex h-2.5 w-2.5 flex-none">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
      </span>
      <div className="flex items-center gap-1.5 truncate">
        <WifiOff size={15} className="text-amber-400 flex-none" />
        <span className="font-semibold text-slate-100">
          {t(language, 'offline_pill_label') || 'Offlineläge:'}
        </span>
        <span className="text-slate-300 truncate">
          {t(language, 'offline_pill_desc') || 'Visar lokalt sparade artiklar'}
        </span>
      </div>
    </aside>
  );
};

export default OfflineScreen;